import { memo } from 'react';
import {
  Checkbox,
  FormField,
  Input,
  Multiselect,
  Select,
  SelectProps,
} from '@amzn/awsui-components-core';

import { ContentUIComponent } from '../../model/Enum';
import { MetadataField } from '../../services/MetadataFieldOperations';
import { MetadataKeyOptionsMap } from '../utils/SelectableOptions';

interface MetadataTabFieldInputViewProps {
  readonly field: MetadataField;
  readonly errorText: string | undefined;
  readonly onChange: (next: unknown) => void;
}

/** Renders one schema-driven metadata field as the appropriate Cloudscape input. */
export const MetadataTabFieldInputView = memo(function MetadataTabFieldInputView({
  field,
  errorText,
  onChange,
}: MetadataTabFieldInputViewProps) {
  const options = MetadataKeyOptionsMap.get(field.key) ?? [];

  const handleSelect = ({ detail }: { detail: SelectProps.ChangeDetail }) =>
    onChange(detail.selectedOption?.value);

  const handleMultiselect = ({
    detail,
  }: {
    detail: { selectedOptions: readonly SelectProps.Option[] };
  }) =>
    onChange(
      detail.selectedOptions.map((o) => o.value).filter((v): v is string => v !== undefined),
    );

  // The label lives on the surrounding KeyValuePairs row, so the inner FormField
  // only carries description + error text to avoid rendering the same label twice.
  switch (field.uiComponent) {
    case ContentUIComponent.MetadataSelectInput: {
      const selected = options.find((o) => o.value === field.value) ?? null;
      return (
        <FormField description={field.description} errorText={errorText} stretch>
          <Select
            selectedOption={selected}
            options={options}
            placeholder={`Choose ${field.label}`}
            onChange={handleSelect}
          />
        </FormField>
      );
    }
    case ContentUIComponent.MetadataMultiselectInput: {
      const currentValues = (field.value as string[] | undefined) ?? [];
      const selected = options.filter(
        (o) => o.value !== undefined && currentValues.includes(o.value),
      );
      return (
        <FormField description={field.description} errorText={errorText} stretch>
          <Multiselect
            selectedOptions={selected}
            options={options}
            placeholder={`Choose ${field.label}`}
            onChange={handleMultiselect}
          />
        </FormField>
      );
    }
    case ContentUIComponent.MetadataStringInput: {
      return (
        <FormField description={field.description} errorText={errorText} stretch>
          <Input
            value={(field.value as string | undefined) ?? ''}
            placeholder={`Enter ${field.label}`}
            onChange={({ detail }) => onChange(detail.value)}
          />
        </FormField>
      );
    }
    case ContentUIComponent.MetadataCheckboxInput: {
      return (
        <FormField description={field.description} errorText={errorText} stretch>
          <Checkbox checked={!!field.value} onChange={({ detail }) => onChange(detail.checked)} />
        </FormField>
      );
    }
    default:
      return null;
  }
});
